import { css } from '@emotion/react'
import styled from '@emotion/styled'
import * as constants from 'view/styles/constants'
import * as mixins from 'view/styles/mixins'
import { MultiButton, MultiButtonProps } from './MultiButton'

export const textButtonMixin = () => css`
  ${mixins.noWrap}
  display: block;
  border: none;
  height: 26px;
  min-width: 90px;
  border-radius: 6px;
  border-width: 2px;
  border-style: solid;
  padding: 0 10px;
  background-color: transparent;
  font-weight: ${constants.FontWeight.SemiBold};
  font-size: 12px;
  &[disabled],
  &[href='javascript:void(0)'] {
    color: ${constants.CORE_PRIMARY};
    border-color: ${constants.CORE_PRIMARY_3};
    background-color: ${constants.CORE_PRIMARY_3};
  }
`

const primaryButtonColoursMixin = css`
  color: ${constants.CORE_PRIMARY};
  border-color: ${constants.ACCENT_PRIMARY};
  background-color: ${constants.ACCENT_PRIMARY};
  &:active,
  &:hover {
    border-color: ${constants.ACCENT_PRIMARY_1};
    background-color: ${constants.ACCENT_PRIMARY_1};
  }
`

export const secondaryButtonColoursMixin = css`
  color: ${constants.CORE_PRIMARY_7};
  border-color: ${constants.ACCENT_PRIMARY_3};
  background-color: ${constants.CORE_PRIMARY};
  &:active,
  &:hover {
    border-color: ${constants.ACCENT_PRIMARY_1};
  }
  &:active {
    color: ${constants.CORE_PRIMARY};
    background-color: ${constants.ACCENT_PRIMARY_1};
  }
`

const tertiaryButtonColoursMixin = css`
  color: ${constants.CORE_PRIMARY_7};
  border-color: ${constants.CORE_PRIMARY_3};
  background-color: ${constants.CORE_PRIMARY};
  &:active,
  &:hover {
    border-color: ${constants.CORE_PRIMARY_5};
  }
  &:active {
    color: ${constants.CORE_PRIMARY};
    border-color: ${constants.ACCENT_PRIMARY_1};
    background-color: ${constants.ACCENT_PRIMARY_1};
  }
`

const goodButtonColoursMixin = css`
  color: ${constants.CORE_PRIMARY};
  border-color: ${constants.ACCENT_GOOD};
  background-color: ${constants.ACCENT_GOOD};
  &:active,
  &:hover {
    border-color: ${constants.ACCENT_GOOD_1};
    background-color: ${constants.ACCENT_GOOD_1};
  }
`

const badButtonColoursMixin = css`
  color: ${constants.CORE_PRIMARY};
  border-color: ${constants.ACCENT_BAD};
  background-color: ${constants.ACCENT_BAD};
  &:active,
  &:hover {
    border-color: ${constants.ACCENT_BAD_1};
    background-color: ${constants.ACCENT_BAD_1};
  }
`

const badLightButtonColoursMixin = css`
  color: ${constants.CORE_PRIMARY};
  border-color: ${constants.ACCENT_BAD_2};
  background-color: ${constants.ACCENT_BAD_2};
  &:active,
  &:hover {
    border-color: ${constants.ACCENT_BAD_1};
    background-color: ${constants.ACCENT_BAD_1};
  }
`

const badOutlineButtonColoursMixin = css`
  color: ${constants.ACCENT_BAD_1};
  border-color: ${constants.ACCENT_BAD_1};
  background-color: ${constants.CORE_PRIMARY};
  &:active,
  &:hover {
    background-color: ${constants.ACCENT_BAD};
    color: ${constants.CORE_PRIMARY};
  }
`

const warnButtonColoursMixin = css`
  color: ${constants.CORE_PRIMARY};
  border-color: ${constants.ACCENT_BE_AWARE};
  background: ${constants.ACCENT_BE_AWARE};
  &:active,
  &:hover {
    border-color: ${constants.ACCENT_BE_AWARE_1};
    background-color: ${constants.ACCENT_BE_AWARE_1};
  }
`

export const primaryButton = css`
  ${textButtonMixin()};
  ${primaryButtonColoursMixin};
`

const secondaryButton = css`
  ${textButtonMixin()};
  ${secondaryButtonColoursMixin};
`

const tertiaryButton = css`
  ${textButtonMixin()};
  ${tertiaryButtonColoursMixin};
`

const goodButton = css`
  ${textButtonMixin()};
  ${goodButtonColoursMixin};
`

const badButton = css`
  ${textButtonMixin()};
  ${badButtonColoursMixin};
`

const badLightButton = css`
  ${textButtonMixin()};
  ${badLightButtonColoursMixin};
`

const badOutlineButton = css`
  ${textButtonMixin()};
  ${badOutlineButtonColoursMixin};
`

const warnButton = css`
  ${textButtonMixin()};
  ${warnButtonColoursMixin};
`

export enum TextButtonType {
  Primary = 'Primary',
  Secondary = 'Secondary',
  Tertiary = 'Tertiary',
  Warn = 'Warn',
  Good = 'Good',
  Bad = 'Bad',
  BadLight = 'BadLight',
  BadOutline = 'BadOutline'
}

export interface TextButtonProps extends MultiButtonProps {
  type: TextButtonType
}

const buttonStyle = {
  [TextButtonType.Primary]: primaryButton,
  [TextButtonType.Secondary]: secondaryButton,
  [TextButtonType.Tertiary]: tertiaryButton,
  [TextButtonType.Good]: goodButton,
  [TextButtonType.Bad]: badButton,
  [TextButtonType.BadLight]: badLightButton,
  [TextButtonType.BadOutline]: badOutlineButton,
  [TextButtonType.Warn]: warnButton
}

export const TextButton = styled(MultiButton)<TextButtonProps>`
  ${props => buttonStyle[props.type]};
`
